import { REGISTER_COLUMNS_BY_TYPE } from '@Tags/config/registerColumns';
import TagsRegisterService from '@Tags/services/tagsRegister.service';
import { ForgeEquipment, ForgePipeline, ForgeStructural, ForgeBaseTag } from '@/scripts/js/forgeModels';
import { log, warn, error } from '@/utils/debugLogger';
import { columnFilterParams, isRequestCancelled } from '@Tags/utils/registerLoadPolicy';

/**
 * Maps raw tag data to the appropriate Forge model class.
 * This ensures all computed properties (availableAssets, etc.) are set via parseContent().
 * @param {Object} rawTag - Raw tag from API
 * @param {string} tagType - 'Equipment', 'Pipeline', or 'Structural'
 * @returns {ForgeBaseTag} - Fully initialized Forge model instance
 */
function mapTagToForgeModel(rawTag, tagType) {
  let forgeTag;
  switch (tagType) {
    case 'Equipment':
      forgeTag = Object.assign(new ForgeEquipment(), rawTag);
      break;
    case 'Pipeline':
      forgeTag = Object.assign(new ForgePipeline(), rawTag);
      break;
    case 'Structural':
      forgeTag = Object.assign(new ForgeStructural(), rawTag);
      break;
    default:
      forgeTag = Object.assign(new ForgeBaseTag(), rawTag);
  }
  forgeTag.parseContent();
  return forgeTag;
}

export const TAGS_REGISTER = {
  namespaced: true,
  state: {
    // Column configurations for each tag type.
    // Copied, not referenced: SET_COLUMN_CONFIG writes into this object per type.
    columnConfigs: { ...REGISTER_COLUMNS_BY_TYPE },

    entries: [],
    total: 0,
    loading: false,
    updateLoading: false,   
    options: {
      itemsPerPage: 15,
      sortBy: ['name'],
      sortDesc: [false],
    },
    
    // Isolated tags for 3D viewer
    isolatedTags: [],

    // Current request cancellation token
    currentRequestController: null,

    // ViewersSection state
    viewerOpened: false,
    viewerTab: '3D View',

    // Link-status filter: subset of ['Linked', 'Other', 'NoLink'].
    //   Linked  = tag has children or position in THIS facility (manuals always qualify).
    //   Other   = linked on another facility in same FacilityGroup and NOT linked here.
    //   NoLink  = unharvested master with no position anywhere.
    // Default: show Linked + Other (the two most common working views).
    linkStatusFilter: ['Linked', 'Other'],

    // Per-column filters of the register table: { column: { type: 'search'|'select', value } }.
    // Kept here (not in TagsRegister.vue) so every load sends them, including the parent's
    // loads for link-status toggles and register switches.
    columnFilters: {}
  },
  
  getters: {
    GET_COLUMN_CONFIG: (state) => (tagType) => {
      return state.columnConfigs[tagType] || [];
    },
    
    GET_ENTRIES: (state) => {
      return state.entries
    },

    GET_TOTAL: (state) => {
      return state.total;
    },
    
    GET_LOADING: (state) => {
      return state.loading
    },
    
    GET_OPTIONS: (state) => {
      return state.options;
    },
    
    GET_ISOLATED_TAGS: (state) => {
      return state.isolatedTags;
    },

    GET_CURRENT_REQUEST_CONTROLLER: (state) => {
      return state.currentRequestController;
    },

    GET_VIEWER_OPENED: (state) => {
      return state.viewerOpened;
    },

    GET_VIEWER_TAB: (state) => {
      return state.viewerTab;
    },

    GET_LINK_STATUS_FILTER: (state) => {
      return state.linkStatusFilter;
    },

    GET_COLUMN_FILTERS: (state) => {
      return state.columnFilters;
    }
  },
  
  mutations: {
    SET_ENTRIES(state, value) {
      state.entries = value;
    },

    SET_TOTAL(state, value) {
      state.total = value;
    },
    
    SET_LOADING(state, value) {
      state.loading = value;
    },

    SET_UPDATE_LOADING(state, value) {
      state.updateLoading = value;
    },
    
    SET_OPTIONS(state, options) {
      state.options = { ...state.options, ...options };
    },
    
    UPDATE_TAG_IN_CACHE(state, { tagId, field, value }) {
      const tags = state.entries || [];
      const tagIndex = tags.findIndex(tag => tag.id === tagId);
      if (tagIndex !== -1) {
        state.entries[tagIndex][field] = value;
      }
    },

    UPDATE_TAG_MULTIPLE_FIELDS_IN_CACHE(state, { tagId, changes }) {
      const tags = state.entries || [];
      const tagIndex = tags.findIndex(tag => tag.id === tagId);
      if (tagIndex !== -1) {
        Object.keys(changes).forEach(field => {
          state.entries[tagIndex][field] = changes[field];
        });
      }
    },

    UPDATE_TAG_CUSTOM_ATTRIBUTES_IN_CACHE(state, { tagId, customAttributes }) {
      const tags = state.entries || [];
      const tagIndex = tags.findIndex(tag => tag.id === tagId);
      if (tagIndex !== -1) {
        const tag = state.entries[tagIndex];

        // Parse existing custom attributes from JSON (the actual data property)
        let existingAttrs = {};
        try {
          existingAttrs = tag.customAttributesJson
            ? JSON.parse(tag.customAttributesJson)
            : {};
        } catch (e) {
          existingAttrs = {};
        }

        // Merge new attributes
        Object.keys(customAttributes).forEach(attrName => {
          existingAttrs[attrName] = customAttributes[attrName];
        });

        // Update the JSON string (the actual data property)
        tag.customAttributesJson = JSON.stringify(existingAttrs);

        // Force Vue reactivity by replacing the array entry with splice()
        // Use Object.assign with Object.create to preserve prototype chain (keeps customAttributes getter)
        const newTag = Object.assign(Object.create(Object.getPrototypeOf(tag)), tag);
        state.entries.splice(tagIndex, 1, newTag);
      }
    },
    
    REMOVE_TAG_FROM_CACHE(state, { tagId }) {
      const tags = state.entries || [];
      state.entries = tags.filter(tag => tag.id !== tagId);
    },
    
    ADD_TAG_TO_CACHE(state, { tag }) {
      if (!state.entries) {
        state.entries = [];
      }
      state.entries.push(tag);
    },
    
    SET_ISOLATED_TAGS(state, tags) {
      state.isolatedTags = tags;
    },

    SET_COLUMN_CONFIG(state, { tagType, config }) {
      state.columnConfigs[tagType] = config;
    },

    SET_REQUEST_CONTROLLER(state, controller) {
      state.currentRequestController = controller;
    },

    CLEAR_REQUEST_CONTROLLER(state) {
      state.currentRequestController = null;
    },

    SET_VIEWER_OPENED(state, value) {
      state.viewerOpened = value;
    },

    SET_VIEWER_TAB(state, value) {
      state.viewerTab = value;
    },

    SET_LINK_STATUS_FILTER(state, value) {
      state.linkStatusFilter = value;
    },

    SET_COLUMN_FILTERS(state, value) {
      state.columnFilters = value || {};
    }
  },
  
  actions: {
    async LOAD_TAGS({ commit, state }, { tagType, assetId, options = {} }) {
      // Cancel any existing request
      if (state.currentRequestController) {
        state.currentRequestController.abort();
      }

      // Create new AbortController for this request
      const controller = new AbortController();
      commit('SET_REQUEST_CONTROLLER', controller);
      commit('SET_LOADING', true);
      
      try {
        let params = {
          ...state.options,
          ...options,
          sortBy: state.options.sortBy?.length > 0 ? state.options.sortBy[0] : null,
          sortDesc: state.options.sortDesc?.length > 0 ? state.options.sortDesc[0] : false,
          linkStatus: state.linkStatusFilter,
          // Callers may pass their own (e.g. {} to load without filters); otherwise the active ones.
          columnFilters: options.columnFilters ?? columnFilterParams(state.columnFilters),
        }

        const response = await TagsRegisterService.getTagsByType(tagType, assetId, params, controller.signal);

        // Only the newest request may touch the table: a newer load (or a cancel) has replaced
        // this controller, and that request's page must not be overwritten by this older one.
        if (state.currentRequestController !== controller) {
          return response.data;
        }

        // Handle paginated response
        const rawTags = response.data.items || response.data;

        // Map raw tags to Forge model instances (sets availableAssets, etc.)
        const tags = rawTags.map(tag => mapTagToForgeModel(tag, tagType));
        commit('SET_ENTRIES', tags);
        commit('SET_TOTAL', response.data.total);

        return response.data;
      } catch (err) {
        // Don't log error if request was cancelled
        if (!isRequestCancelled(err)) {
          warn(`Failed to load ${tagType} tags:`, err);
        }
        throw err;
      } finally {
        // Leave loading/controller alone when a newer request owns them: clearing them here let a
        // cancelled request make the newest one un-cancellable (TAG_REGISTER_SEARCH_RACE.md #3).
        if (state.currentRequestController === controller) {
          commit('SET_LOADING', false);
          commit('CLEAR_REQUEST_CONTROLLER');
        }
      }
    },
    
    async UPDATE_TAG({ commit }, { tagId, field, value, tagType, assetId }) {
      try {
        commit('SET_UPDATE_LOADING', true);
        const response = await TagsRegisterService.updateTagField(tagType, assetId, tagId, field, value);
        
        // Update local cache with the response data
        const updatedTag = response.data;
        commit('UPDATE_TAG_IN_CACHE', { tagId, field, value });
        
        return updatedTag;
      } catch (error) {
        error('Failed to update tag:', error);
        throw error;
      } finally {
        commit('SET_UPDATE_LOADING', false);
      }
    },

    async UPDATE_TAG_MULTIPLE({ commit }, { tagId, changes, tagType, assetId }) {
      try {
        commit('SET_UPDATE_LOADING', true);
        const response = await TagsRegisterService.updateTagMultipleFields(tagType, assetId, tagId, changes);

        // Convert cells array back to field-value object for cache update
        const fieldChanges = {};
        if (changes.cells && Array.isArray(changes.cells)) {
          changes.cells.forEach(cell => {
            fieldChanges[cell.ColumnTitle] = cell.Value;
          });
        }

        // Update local cache with all changes (standard fields)
        commit('UPDATE_TAG_MULTIPLE_FIELDS_IN_CACHE', { tagId, changes: fieldChanges });

        // Update custom attributes in cache if present
        if (changes.customAttributes) {
          commit('UPDATE_TAG_CUSTOM_ATTRIBUTES_IN_CACHE', {
            tagId,
            customAttributes: changes.customAttributes
          });
        }

        return response.data;
      } catch (error) {
        error('Failed to update multiple tag fields:', error);
        throw error;
      } finally {
        commit('SET_UPDATE_LOADING', false);
      }
    },
    
    async DELETE_TAG({ commit }, { tagId, tagType, assetId }) {
      try {
        await TagsRegisterService.deleteTag(tagType, assetId, tagId);
        
        // Remove from local cache
        commit('REMOVE_TAG_FROM_CACHE', { tagId });
        
      } catch (error) {
        error('Failed to delete tag:', error);
        throw error;
      }
    },

    CANCEL_CURRENT_REQUEST({ commit, state }) {
      if (state.currentRequestController) {
        state.currentRequestController.abort();
        commit('CLEAR_REQUEST_CONTROLLER');
        commit('SET_LOADING', false);
      }
    }
  }
};


